/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

/**
 * Build the `callback_url` passed to initPayment. Single source of truth for callback URL
 * construction - to add a field, extend SUPPORTED_FIELDS and parse it in useWidgetCallbackParams
 * (consumed by useSchedulerInitialization).
 *
 * Fields: service / location / provider (ids to preselect), coupon (promo code).
 *
 * Not framed -> `origin + pathname + "#sb-widget-callback/..."`; the reloaded page reads its own
 * hash.
 *
 * Framed (`window.self !== window.top`, or origin "null" for an `<iframe srcdoc>` embed) ->
 * window.location describes the iframe's document, not the page the client sees, so it is never a
 * usable return address. Falls back to react_widget_notification_url, else undefined so the
 * backend uses its own default. Deciding this here keeps callback_url a plain generic value to
 * the API.
 *
 * The framed branch appends a PATH, not a hash: the partner's server reads it and re-embeds the
 * widget with a matching hash, and a hash never reaches a server. Same form
 * getPublicCompanyClientDataLink() already uses.
 */
const SUPPORTED_FIELDS = ["service", "location", "provider", "coupon"];

export const isWidgetFramed = () => window.self !== window.top || window.location.origin === "null";

export const buildCallbackUrl = (params = {}, widgetNotificationUrl = null) => {
  let base;

  if (isWidgetFramed()) {
    if (!widgetNotificationUrl) return undefined;
    // Strip trailing "/" only, matching getPublicCompanyClientDataLink()'s rtrim - not "#". A
    // partner URL ending in "#" makes this a browser-only fragment; that is their choice.
    base = widgetNotificationUrl.replace(/\/+$/, "") + "/sb-widget-callback/";
  } else {
    base = window.location.origin + window.location.pathname + "#sb-widget-callback/";
  }

  const parts = [];

  SUPPORTED_FIELDS.forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    parts.push(`${key}/${encodeURIComponent(String(value))}`);
  });

  if (parts.length === 0) return base;

  return `${base}${parts.join("/")}/`;
};

/**
 * This document's own address, minus any hash. Keeps the query string - an embed iframe's src
 * often carries params the host needs to render it again (Wix). Empty for an `<iframe srcdoc>`
 * embed, whose opaque origin has no address to return to.
 */
export const buildFrameReturnUrl = () => {
  if (!window.location.origin || window.location.origin === "null") return "";
  return window.location.href.split("#")[0];
};

/**
 * Relay page URL used as callback_url for the popup flow.
 *
 * `frame` (this document) and `fallback` (config.company_link) are the relay's two no-opener
 * destinations - see payment-relay.php for which is used when.
 *
 * `data` MUST stay last: the backend appends "ext/invoice-payment/{step}/..." to whatever
 * callback_url it gets, so only a trailing parameter captures that suffix in its own value.
 */
export const buildRelayUrl = (config) => {
  if (!config?.react_widget_assets_url) return null;
  const fallback = encodeURIComponent(config.company_link || "");
  const frame = encodeURIComponent(buildFrameReturnUrl());
  return `${config.react_widget_assets_url}payment-relay.php?fallback=${fallback}&frame=${frame}&data=`;
};

/**
 * Origin the relay's postMessage must come from. The relay broadcasts with "*" (it can't know its
 * opener), so this is the only thing that separates a real payment-complete message from a forged
 * one - keep it derived here rather than re-parsed per call site.
 */
export const buildRelayOrigin = (config) => {
  if (!config?.react_widget_assets_url) return null;
  try {
    return new URL(config.react_widget_assets_url).origin;
  } catch (e) {
    return null;
  }
};
